import ArrowCornerRightIcon from '@assets/icons/arrow-corner-right.svg';
import EmployeesIcon from '@assets/icons/employees.svg';
import i18n from '@/utils/i18n';
import NoteIcon from '@assets/icons/note.svg';
import OrdersIcon from '@assets/icons/orders.svg';
import PencilIcon from '@assets/icons/pencil.svg';
import React, { useMemo, useState } from 'react';
import { AccountDetailAccountNotes } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-account-notes';
import { AccountDetailARCard } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-ar-card';
import { AccountDetailCheckIn } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/account-detail-check-in/account-detail-check-in';
import { AccountDetailContent } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-content';
import { AccountDetailHoursDropdown } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-hours-dropdown';
import { AccountDetailPhoneNumber } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-phone-number';
import { ActivityNotePanel } from '@/experiences/sales-portal/containers/activity-note-panel/activity-note-panel';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { ContentWithPanelTemplate } from '@experiences/templates/content-with-panel-template';
import { createStyles } from '@/contexts/theme-context';
import { EditAccountPanel } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/edit-account-panel/edit-account-panel';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { formatDateTimeWithYear } from '@/utils/date';
import { IconButton, Icons } from '@usepepper/sprout-kit-ui';
import { MobileHours } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/mobile-hours';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from 'react-native';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import { OnlineMenuModal } from '@/experiences/sales-portal/containers/create-order-guide/online-menu-modal';
import {
  QuickActionItem,
  QuickActionsCard,
} from '@/experiences/sales-portal/components/quick-actions-card';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalAnalyticEvent } from '@/experiences/sales-portal/analytics/fennel/registry';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { track } from '@/experiences/sales-portal/analytics/fennel/hook';
import { UploadPhotoOrderGuideModalContainer } from '@/experiences/sales-portal/containers/create-order-guide/upload-photo-order-guide-modal-container';
import { useAccountDetailARLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-detail-ar-loader';
import { useAccountDetailMetricsLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-detail-metrics-loader';
import { useAccountDetailSalesAlertsLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-detail-sales-alerts-loader';
import { useAccountDetailsLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-details-loader';
import { useAccountItemsOfInterestLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-items-of-interest-loader';
import { useActivityNotesLoader } from '@/experiences/sales-portal/blocs/loaders/use-activity-notes-loader';
import { useBottomNavScrollHandler } from '@sales-portal/blocs/hooks/use-bottom-nav-scroll-handler';
import { useBusinessPhoneNumberLoader } from '@/experiences/sales-portal/blocs/loaders/use-business-phone-number-loader';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useCreateOrderGuideQuickAction } from '@/experiences/sales-portal/containers/create-order-guide/blocs/hooks/use-create-order-guide-quick-action';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';
import { useNavigation } from '@react-navigation/native';

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.AccountDetail
>;

const NEAR_END_THRESHOLD_PX = 50;
const DESKTOP_SCROLL_EVENT_THROTTLE_MS = 100;
const MOBILE_SCROLL_EVENT_THROTTLE_MS = 16;
const MOBILE_BOTTOM_NAV_CLEARANCE_PX = 96;
const BACK_ICON_SIZE = 24;
const QUICK_ACTIONS_SKELETON_ROW_COUNT = 6;

enum RightPanelAction {
  ADD_ACTIVITY_NOTE = 'ADD_ACTIVITY_NOTE',
  EDIT_ACCOUNT = 'EDIT_ACCOUNT',
  NONE = 'NONE',
}

export const AccountDetailScreen: React.FC<Props> = ({ route }) => {
  const { restaurantUUID, supplierUUID } = route.params;
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const { switchToExperience } = useExperienceStore();
  const [styles] = useStyles();

  const [noteCreatedAt, setNoteCreatedAt] = useState(
    formatDateTimeWithYear(new Date()),
  );
  const [rightPanelAction, setRightPanelAction] = useState<RightPanelAction>(
    RightPanelAction.NONE,
  );

  const handleOpenEditPanel = () => {
    setRightPanelAction(RightPanelAction.EDIT_ACCOUNT);
  };
  const handleClosePanel = () => {
    setRightPanelAction(RightPanelAction.NONE);
  };
  const handleOpenActivityNotePanel = () => {
    setNoteCreatedAt(formatDateTimeWithYear(new Date()));
    setRightPanelAction(RightPanelAction.ADD_ACTIVITY_NOTE);
  };

  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();
  const bottomNavScrollHandler = useBottomNavScrollHandler();

  const {
    accountId,
    address,
    businessHours,
    chatUUID,
    isProspect,
    loading,
    notes,
    refetch,
    restaurant,
    showARPanel,
  } = useAccountDetailsLoader({
    restaurantUUID,
    supplierUUID,
  });

  const { value: salesHubCheckInEnabled } =
    useChatSalesPortalDynamicConfigValue(
      supplierUUID,
      'sales_hub_check_in_enabled',
      chatUUID,
    );
  const isCheckInEnabled = salesHubCheckInEnabled === true;

  const showCheckIn = isMobileBottomNavLayout && isCheckInEnabled;

  const {
    closeOnlineMenuModal,
    closeUploadPhotoModal,
    isOnlineMenuModalOpen,
    isUploadPhotoModalOpen,
    loading: createOrderGuideLoading,
    quickAction: createOrderGuideAction,
  } = useCreateOrderGuideQuickAction({
    chatUUID,
    label: i18n.t(
      'sales_portal.account_detail.quick_actions.create_order_guide',
    ),
    onOpenChange: (open) => {
      if (open) {
        track(SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_PRESS, {
          restaurantUUID,
          supplierUUID,
        });
      }
    },
    onSelectOnlineMenu: () =>
      track(SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_ONLINE_MENU_PRESS, {
        restaurantUUID,
        supplierUUID,
      }),
    onSelectUploadPhoto: () =>
      track(SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_UPLOAD_PHOTO_PRESS, {
        restaurantUUID,
        supplierUUID,
      }),
    supplierUUID,
  });
  const showStartOrderAction = !isProspect;

  const { error: phoneNumberError, phoneNumber } = useBusinessPhoneNumberLoader(
    {
      restaurantUUID,
      supplierUUID,
    },
  );

  const {
    error: metricsError,
    metrics,
    onTimeRangeChange,
    selectedTimeRange,
  } = useAccountDetailMetricsLoader({
    restaurantUUID,
    supplierUUID,
  });
  const { error: itemsOfInterestError, items: itemsOfInterest } =
    useAccountItemsOfInterestLoader({
      restaurantUUID,
      supplierUUID,
    });

  const {
    activityNotes,
    error: activityNotesError,
    fetchMore: fetchMoreActivityNotes,
    fetchingMore: fetchingMoreActivityNotes,
    loading: loadingActivityNotes,
    refetch: refetchActivityNotes,
  } = useActivityNotesLoader({
    restaurantUUID,
    supplierUUID,
  });
  const { salesAlerts } = useAccountDetailSalesAlertsLoader({
    restaurantUUID,
    supplierUUID,
  });

  const {
    error: arError,
    loading: arLoading,
    overdueBalanceInMicros,
    totalBalanceInMicros,
  } = useAccountDetailARLoader({
    enabled: showARPanel,
    restaurantUUID,
    supplierUUID,
  });

  const coordinate = address
    ? { latitude: address.latitude, longitude: address.longitude }
    : undefined;

  const markers = coordinate
    ? [
        {
          accessibilityLabel: restaurant?.name
            ? i18n.t('sales_portal.account_detail.map_marker_label', {
                restaurantName: restaurant.name,
              })
            : undefined,
          coordinate,
          label: restaurant?.name,
        },
      ]
    : undefined;

  const quickActionsLoading = loading || createOrderGuideLoading;

  const quickActions: QuickActionItem[] = [
    ...(showStartOrderAction
      ? [
          {
            Icon: OrdersIcon,
            label: i18n.t(
              'sales_portal.account_detail.quick_actions.start_order',
            ),
            onPress: () => {
              track(SalesPortalAnalyticEvent.START_ORDER_PRESS, {
                restaurantUUID,
                supplierUUID,
              });
              navigateToSalesPortalRoute(
                navigation,
                SALES_PORTAL_ROUTES.OrderEntry,
                {
                  restaurantUUID,
                  supplierUUID,
                },
              );
            },
          },
        ]
      : []),
    ...(createOrderGuideAction ? [createOrderGuideAction] : []),
    ...(chatUUID
      ? [
          {
            Icon: NoteIcon,
            label: i18n.t(
              'sales_portal.account_detail.quick_actions.add_activity_note',
            ),
            onPress: handleOpenActivityNotePanel,
          },
        ]
      : []),
    {
      Icon: EmployeesIcon,
      label: i18n.t(
        'sales_portal.account_detail.quick_actions.employee_management',
      ),
      onPress: () => {
        navigation.push(SALES_PORTAL_ROUTES.EmployeeList, {
          restaurantUUID,
          supplierUUID,
        });
      },
    },
    {
      Icon: PencilIcon,
      label: i18n.t('sales_portal.account_detail.quick_actions.edit_customer'),
      onPress: handleOpenEditPanel,
    },
    {
      Icon: ArrowCornerRightIcon,
      label: i18n.t(
        'sales_portal.account_detail.quick_actions.view_in_operator',
      ),
      onPress: () => {
        switchToExperience(ExperienceMode.OPERATOR_PORTAL);
      },
    },
  ];

  const rightPanelContent = useMemo(() => {
    switch (rightPanelAction) {
      case RightPanelAction.EDIT_ACCOUNT:
        return (
          <EditAccountPanel
            onAccountSave={refetch}
            onClose={handleClosePanel}
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
        );
      case RightPanelAction.ADD_ACTIVITY_NOTE:
        return chatUUID ? (
          <ActivityNotePanel
            chatUUID={chatUUID}
            createdAt={noteCreatedAt}
            onClose={handleClosePanel}
            onNoteSaved={refetchActivityNotes}
          />
        ) : null;
      case RightPanelAction.NONE:
      default:
        return null;
    }
  }, [
    chatUUID,
    noteCreatedAt,
    refetch,
    refetchActivityNotes,
    restaurantUUID,
    rightPanelAction,
    supplierUUID,
  ]);

  // Owns its body scroll (`scroll={false}`) so the activity-notes feed can
  // paginate on near-end.
  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isMobileBottomNavLayout) {
      bottomNavScrollHandler(event);
    }
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromBottom =
      contentSize.height - (layoutMeasurement.height + contentOffset.y);
    if (distanceFromBottom <= NEAR_END_THRESHOLD_PX) {
      fetchMoreActivityNotes();
    }
  };

  return (
    <SalesHubShellTemplate>
      <ContentWithPanelTemplate
        onClose={handleClosePanel}
        panelContent={rightPanelContent ?? undefined}
      >
        <AppPageLayout
          bodyGutter={!isMobileBottomNavLayout ? undefined : '$0'}
          mode="focused"
          scroll={false}
          subBar={
            !isMobileBottomNavLayout ? null : (
              <IconButton
                aria-label={i18n.t('back')}
                icon={<Icons.ArrowLeft size={BACK_ICON_SIZE} />}
                onPress={() => navigation.goBack()}
                shape="circle"
                variant="tertiary"
              />
            )
          }
          title={
            !isMobileBottomNavLayout
              ? i18n.t('sales_portal.account.title')
              : undefined
          }
        >
          <ScrollView
            accessibilityLabel="account-detail-scroll"
            contentContainerStyle={[
              styles.scrollContent,
              !isMobileBottomNavLayout ? null : styles.mobileScrollContent,
            ]}
            onScroll={handleScroll}
            scrollEventThrottle={
              !isMobileBottomNavLayout
                ? DESKTOP_SCROLL_EVENT_THROTTLE_MS
                : MOBILE_SCROLL_EVENT_THROTTLE_MS
            }
            showsVerticalScrollIndicator={false}
            style={styles.scroll}
          >
            <AccountDetailContent
              accountId={accountId}
              activityNotes={activityNotes}
              activityNotesHasError={Boolean(activityNotesError)}
              additionalHeaderContent={
                <>
                  <AccountDetailPhoneNumber
                    error={phoneNumberError}
                    phoneNumber={phoneNumber}
                  />
                  {isMobileBottomNavLayout ? (
                    <MobileHours {...businessHours} />
                  ) : (
                    <AccountDetailHoursDropdown {...businessHours} />
                  )}
                </>
              }
              address={address}
              coordinate={coordinate}
              fetchingMoreActivityNotes={fetchingMoreActivityNotes}
              isProspect={isProspect}
              itemsOfInterest={itemsOfInterest}
              itemsOfInterestError={itemsOfInterestError}
              loading={loading}
              loadingActivityNotes={loadingActivityNotes}
              markers={markers}
              metrics={metrics}
              metricsError={metricsError}
              onRetryActivityNotes={() => refetchActivityNotes()}
              onTimeRangeChange={onTimeRangeChange}
              restaurant={restaurant}
              salesAlerts={salesAlerts}
              selectedTimeRange={selectedTimeRange}
              sidebarContent={
                <>
                  <QuickActionsCard
                    actions={quickActions}
                    loading={quickActionsLoading}
                    skeletonRowCount={QUICK_ACTIONS_SKELETON_ROW_COUNT}
                  />
                  {showARPanel && !arError ? (
                    <AccountDetailARCard
                      loading={arLoading}
                      overdueBalanceInMicros={overdueBalanceInMicros}
                      totalBalanceInMicros={totalBalanceInMicros}
                    />
                  ) : null}
                  {notes ? <AccountDetailAccountNotes notes={notes} /> : null}
                </>
              }
            />
          </ScrollView>
          {showCheckIn && restaurant && chatUUID ? (
            <AccountDetailCheckIn
              accountName={restaurant.name}
              chatUUID={chatUUID}
              onSaved={() => refetchActivityNotes()}
            />
          ) : null}
          <OnlineMenuModal
            isOpen={isOnlineMenuModalOpen}
            onClose={closeOnlineMenuModal}
            restaurantName={restaurant?.name}
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
          <UploadPhotoOrderGuideModalContainer
            isOpen={isUploadPhotoModalOpen}
            onClose={closeUploadPhotoModal}
            restaurantName={restaurant?.name}
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
        </AppPageLayout>
      </ContentWithPanelTemplate>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  mobileScrollContent: {
    paddingBottom: MOBILE_BOTTOM_NAV_CLEARANCE_PX,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
}));
